import React, { useState } from "react";
import { motion, type Variants, useReducedMotion, type Transition } from "framer-motion";
import { navigateTo } from "../../utils/navigation";

/** Mini animated demo for each issue */
function MiniDemo({ mode, good }: { mode: "dark" | "distance" | "glare" | "blur" | "hair" | "tilt"; good: boolean }) {
  const prefersReduced = useReducedMotion();
  const t: Transition = prefersReduced ? { duration: 0 } : { type: "spring", stiffness: 400, damping: 36 };

  // Shared face (svg)
  const Face = (
    <g>
      {/* head */}
      <motion.circle cx="64" cy="64" r="34" fill="#ffe9d6" stroke="#1F2421" strokeWidth="1" />
      {/* hair fringe (only for hair mode) */}
      <motion.path
        d="M32 50 C48 38, 80 38, 96 50 L96 40 C80 30, 48 30, 32 40 Z"
        fill="#1f2937"
        variants={{
          bad: { opacity: mode === "hair" ? 1 : 0, y: mode === "hair" ? 6 : 0 },
          good: { opacity: 0, y: 0 },
        }}
        initial={false}
        animate={good ? "good" : "bad"}
        transition={t}
      />
      {/* eyes */}
      <motion.circle cx="54" cy="63" r="3" fill="#1F2421" />
      <motion.circle cx="74" cy="63" r="3" fill="#1F2421" />
      {/* glasses glare (glare mode) */}
      <g opacity={mode === "glare" ? 1 : 0}>
        <rect x="46" y="56" width="16" height="12" rx="3" stroke="#1F2421" fill="none" strokeWidth="1" />
        <rect x="66" y="56" width="16" height="12" rx="3" stroke="#1F2421" fill="none" strokeWidth="1" />
        <motion.rect
          x="46"
          y="56"
          width="36"
          height="12"
          rx="3"
          fill="#F8F4EE"
          variants={{ bad: { opacity: 0.65 }, good: { opacity: 0 } }}
          initial={false}
          animate={good ? "good" : "bad"}
          transition={t}
        />
      </g>
      {/* mouth */}
      <motion.path
        d="M52 78 C58 82, 70 82, 76 78"
        stroke="#1F2421"
        strokeWidth="2"
        fill="none"
        variants={{
          bad: { pathLength: 0.7, opacity: 0.7 },
          good: { pathLength: 1, opacity: 1 },
        }}
        initial={false}
        animate={good ? "good" : "bad"}
        transition={t}
      />
    </g>
  );

  // Scene variants per mode
  const rootV: Variants = {
    bad: {},
    good: {},
  };

  // Extras per mode
  const overlays = {
    dark: (
      <motion.rect
        x="0"
        y="0"
        width="128"
        height="128"
        rx="12"
        fill="#000"
        variants={{ bad: { opacity: 0.5 }, good: { opacity: 0.05 } }}
        initial={false}
        animate={good ? "good" : "bad"}
        transition={{ duration: 0.35 }}
      />
    ),
    distance: null,
    glare: null,
    blur: (
      <motion.g
        style={{ filter: "blur(3px)" }}
        variants={{ bad: { opacity: 1 }, good: { opacity: 0 } }}
        initial={false}
        animate={good ? "good" : "bad"}
        transition={{ duration: 0.35 }}
      >
        {/* subtle duplicate of face to imply blur */}
        <circle cx="64" cy="64" r="34" fill="#000" opacity="0.05" />
      </motion.g>
    ),
    hair: null,
    tilt: null,
  } as const;

  const distanceV: Variants =
    mode === "distance"
      ? {
          bad: { scale: 1.35, y: 6 },
          good: { scale: 1, y: 0 },
        }
      : { bad: {}, good: {} };

  const tiltV: Variants =
    mode === "tilt"
      ? {
          bad: { rotate: -10, x: -4 },
          good: { rotate: 0, x: 0 },
        }
      : { bad: {}, good: {} };

  const blurV: Variants =
    mode === "blur"
      ? {
          bad: { filter: "blur(2px)" },
          good: { filter: "blur(0px)" },
        }
      : { bad: {}, good: {} };

  return (
    <motion.svg
      viewBox="0 0 128 128"
      className="w-32 h-32 rounded-xl"
      style={{ border: '1px solid var(--rule)', background: 'var(--c-cream)' }}
      variants={rootV}
      initial={false}
      animate={good ? "good" : "bad"}
    >
      {/* background */}
      <rect x="0" y="0" width="128" height="128" rx="12" fill="#F8F4EE" />
      {/* guide frame */}
      <rect x="16" y="16" width="96" height="96" rx="10" fill="#fff" stroke="rgba(31,36,33,.12)" />
      {/* face group with per-mode transforms */}
      <motion.g variants={distanceV} transition={t}>
        <motion.g variants={tiltV} transition={t}>
          <motion.g variants={blurV} transition={t}>
            {Face}
          </motion.g>
        </motion.g>
      </motion.g>
      {/* overlays (e.g., dark) */}
      {overlays[mode]}
    </motion.svg>
  );
}

function TroubleCard({
  title,
  problem,
  fix,
  mode,
}: {
  title: string;
  problem: string;
  fix: string;
  mode: "dark" | "distance" | "glare" | "blur" | "hair" | "tilt";
}) {
  const [showFix, setShowFix] = useState(false);
  return (
    <div className="rounded-2xl p-4" style={{ border: '1px solid var(--rule)', background: 'var(--c-cream)', boxShadow: 'var(--sh-lifted)' }}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{title}</div>
        <button
          onClick={() => { setShowFix((s) => !s); }}
          className="btn btn--ghost btn--sm"
        >
          {showFix ? "Show issue" : "Show fix"}
        </button>
      </div>

      <div className="mt-3 flex items-center gap-4">
        <MiniDemo mode={mode} good={showFix} />
        <div className="flex-1 space-y-2 text-sm">
          {!showFix ? (
            <>
              <div className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold" style={{ background: 'rgba(244,168,142,.12)', color: '#C8705A' }}>
                <span>✕</span> Issue
              </div>
              <p style={{ color: 'var(--ink-muted)' }}>{problem}</p>
            </>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold" style={{ background: 'rgba(42,164,166,.10)', color: 'var(--c-deep-teal)' }}>
                <span>✓</span> Fix
              </div>
              <p style={{ color: 'var(--ink-muted)' }}>{fix}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function A12Troubleshooting({ className = "" }: { className?: string }) {
  return (
    <section className={`rounded-2xl p-5 sm:p-6 ${className}`} style={{ border: '1px solid var(--rule)', background: 'var(--c-cream)', boxShadow: 'var(--sh-lifted)' }}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold" style={{ color: 'var(--ink)' }}>Troubleshooting & Do-overs</h2>
        <div className="text-xs uppercase tracking-widest" style={{ color: 'var(--ink-muted)' }}>Quick fixes for common issues</div>
      </div>

      <p className="mt-2 text-sm" style={{ color: 'var(--ink-muted)' }}>
        If your capture didn’t pass our checks, try these quick fixes. Toggle each card to see the problem vs the ideal
        setup.
      </p>

      <div className="mt-4 grid gap-4 grid-cols-1 md:grid-cols-2">
        <TroubleCard
          title="Too dark"
          mode="dark"
          problem="Lighting is too dim; shadows obscure features and reduce model confidence."
          fix="Face a window or soft light source. Avoid strong backlighting; aim for even illumination."
        />
        <TroubleCard
          title="Too close / too far"
          mode="distance"
          problem="The face doesn’t fit comfortably within the guide frame."
          fix="Hold the camera at arm’s length so your whole face and hairline are visible within the frame."
        />
        <TroubleCard
          title="Glasses glare"
          mode="glare"
          problem="Strong reflections across the lenses hide the eyes."
          fix="Tilt your head slightly or raise the light source. If safe, briefly remove glasses."
        />
        <TroubleCard
          title="Motion blur"
          mode="blur"
          problem="The image looks smeared from movement."
          fix="Brace your elbows or rest the phone on a stable surface. Use the self-timer if needed."
        />
        <TroubleCard
          title="Hair covering features"
          mode="hair"
          problem="Hair partially covers eyes or cheeks, confusing the detector."
          fix="Tuck hair behind your ears and keep your forehead and cheeks clear."
        />
        <TroubleCard
          title="Camera tilt / off-center"
          mode="tilt"
          problem="The face is angled or shifted away from center, making symmetry checks harder."
          fix="Hold the phone level and center your face in the guide box, looking straight at the camera."
        />
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <button
          onClick={() => { navigateTo("/new"); }}
          className="rounded-xl bg-primary text-white px-4 py-2 text-sm hover:bg-primary/90"
        >
          Retake a photo
        </button>

      </div>

      <p className="mt-3 text-[11px]" style={{ color: 'var(--ink-muted)' }}>
        Tip: If problems persist, switch rooms or try indirect daylight near a window for the most consistent results.
      </p>
    </section>
  );
}
